import React from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import Badge from '../../../components/atoms/Badge';
import Select from '../../../components/widgets/Select';
import RadioButtonsGroup from '../../../components/widgets/RadioButtonsGroup';
import TextField from '../../../components/molecules/TextField';
import { WarningMessage } from '../../../components/widgets';
import FormSection from '../FormSection';
import FormField from '../FormField';
import { ATTRIBUTION_TAB_IDS, CLICK_IMPRESSION_REPORTING_METHODS, TRANSLATE_PREFIX } from '../../consts';
import useConfirmDialog from '../../useConfirmDialog';
import { useTabUpdater } from '../../formUpdaters';
import TrackingLinkParametersTable from './TrackingLinkParametersTable';
import css from './TrackingLinkOptionsTab.css';

const { CLIENT_SIDE, SERVER_SIDE, BOTH } = CLICK_IMPRESSION_REPORTING_METHODS;

const TAB_PREFIX = `${TRANSLATE_PREFIX}.TRACKING_LINK_OPTIONS_TAB`;
const REPORTING_METHODS = [{ value: CLIENT_SIDE, isCommon: true }, { value: SERVER_SIDE }, { value: BOTH }];

const TrackingLinkOptionsTab = ({ translate, readOnly = false }) => {
    const { values } = useFormikContext();
    const updateTab = useTabUpdater(ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS);
    const tabData = values.formData?.[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS] || {};
    const { confirm, dialog } = useConfirmDialog();

    const setTabField = (field, value) => updateTab({ [field]: value });

    const viewThroughOptions = [
        { value: true, label: translate(`${TAB_PREFIX}.YES`) },
        { value: false, label: translate(`${TAB_PREFIX}.NO`) },
    ];

    const selectedViewThrough =
        tabData.view_through_campaigns !== undefined
            ? viewThroughOptions.find(o => o.value === tabData.view_through_campaigns) || null
            : null;

    const selectedMethod = tabData.click_impression_reporting_method;
    const showElaboration = selectedMethod === BOTH;

    const applyReportingMethod = methodId =>
        updateTab({
            click_impression_reporting_method: methodId,
            ...(methodId !== BOTH ? { click_impression_reporting_method_elaboration: undefined } : {}),
        });

    const handleReportingMethodChange = methodId => {
        const elaboration = tabData.click_impression_reporting_method_elaboration;
        const isLeavingBoth = selectedMethod === BOTH && methodId !== BOTH;
        if (isLeavingBoth && elaboration?.trim()) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CLEAR_ELABORATION_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.CLEAR_ELABORATION_TEXT`,
                onConfirm: () => applyReportingMethod(methodId),
            });
            return;
        }
        applyReportingMethod(methodId);
    };

    const reportingMethodItems = REPORTING_METHODS.map(({ value, isCommon }) => ({
        id: value,
        label: `${TAB_PREFIX}.${value.toUpperCase()}_LABEL`,
        description: `${TAB_PREFIX}.${value.toUpperCase()}_DESCRIPTION`,
        tag: isCommon ? (
            <Badge color={null} className={css.commonTag}>
                {translate(`${TAB_PREFIX}.CLIENT_SIDE_TAG`)}
            </Badge>
        ) : (
            undefined
        ),
        checked: selectedMethod === value,
        labelClassName: css.reportingCardLabel,
    }));

    return (
        <FormSection
            titleKey={`${TRANSLATE_PREFIX}.TABS.TRACKING_LINK_OPTIONS`}
            descriptionKey={`${TAB_PREFIX}.DESCRIPTION`}
            iconName="link"
        >
            <WarningMessage
                type="info"
                showIcon={false}
                collapsible
                collapsibleTitle={`${TAB_PREFIX}.INFO_TITLE`}
                defaultExpanded
                message={`${TAB_PREFIX}.INFO_CONTENT`}
            />
            <FormField
                labelKey={`${TAB_PREFIX}.VIEW_THROUGH_LABEL`}
                required={!readOnly}
                subtitleKey={`${TAB_PREFIX}.VIEW_THROUGH_SUBTITLE`}
                anchor="view_through_campaigns"
            >
                <Select
                    options={viewThroughOptions}
                    value={selectedViewThrough}
                    onChange={opt => setTabField('view_through_campaigns', opt ? opt.value : undefined)}
                    placeholder={translate(`${TRANSLATE_PREFIX}.GENERAL_TAB.SELECT_PLACEHOLDER`)}
                    isSearchable={false}
                    isClearable={false}
                    isDisabled={readOnly}
                    className={css.select}
                />
            </FormField>
            <FormField
                labelKey={`${TAB_PREFIX}.CLICK_IMPRESSION_LABEL`}
                required={!readOnly}
                subtitleKey={`${TAB_PREFIX}.CLICK_IMPRESSION_SUBTITLE`}
                fullWidth
                anchor="click_impression_reporting_method"
            >
                <RadioButtonsGroup
                    radioItems={reportingMethodItems}
                    onChange={item => handleReportingMethodChange(item.id)}
                    disabled={readOnly}
                    className={css.reportingMethodGroup}
                    itemsClassName={css.reportingCard}
                    checkmarkClassName={css.reportingCardCheckmark}
                    descriptionClassName={css.reportingCardDescription}
                />
                {showElaboration && (
                    <FormField
                        labelKey={`${TAB_PREFIX}.ELABORATION_LABEL`}
                        required={!readOnly}
                        className={css.elaborationField}
                        fullWidth
                    >
                        <TextField
                            type="textarea"
                            value={tabData.click_impression_reporting_method_elaboration || ''}
                            inputConfig={{
                                placeholder: readOnly ? undefined : translate(`${TAB_PREFIX}.ELABORATION_PLACEHOLDER`),
                                rows: 3,
                            }}
                            onChange={val => setTabField('click_impression_reporting_method_elaboration', val)}
                            disabled={readOnly}
                            containerClass={css.elaborationTextarea}
                            inputClassName={css.elaborationInput}
                        />
                    </FormField>
                )}
            </FormField>
            <div data-error-anchor="tracking_link_parameters" className={css.tableAnchor}>
                <TrackingLinkParametersTable readOnly={readOnly} />
            </div>
            <FormField labelKey={`${TAB_PREFIX}.ADDITIONAL_NOTES_LABEL`} className={css.additionalNotesField} fullWidth>
                <TextField
                    type="textarea"
                    value={tabData.additional_notes || ''}
                    inputConfig={{
                        placeholder: readOnly ? undefined : translate(`${TAB_PREFIX}.ADDITIONAL_NOTES_PLACEHOLDER`),
                        rows: 3,
                    }}
                    onChange={val => setTabField('additional_notes', val)}
                    disabled={readOnly}
                    containerClass={css.additionalNotesTextarea}
                    inputClassName={css.additionalNotesInput}
                />
            </FormField>
            {dialog}
        </FormSection>
    );
};

TrackingLinkOptionsTab.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(TrackingLinkOptionsTab);
